import React from 'react';
import { useQuery } from '@apollo/client';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import addIcon from '../../icons/non-prefilled/16x16/add.svg';
import editIcon from '../../icons/non-prefilled/16x16/edit.svg';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import SidebarSectionIcon from '../../sidebar/SidebarSectionIcon';
import {
  isPremadeSectionId,
  PremadeSections,
  useUpdateSidebarSection,
} from '../../sidebar/SidebarSectionsProvider';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { UIActions } from '../../ui/uiSlice';

const ITEM_FRAGMENT = gql(`
  fragment AddToSectionMenuItem_item on SectionItem {
    ... on Post {
      id
    }
    ... on Topic {
      id
    }
  }
`);

const ADD_TO_SECTION_MENU_ITEM_QUERY = gql(`
  query AddToSectionMenuItem {
    currentSession {
      user {
        id
        sidebarSections {
          id
          name
          items {
            ...AddToSectionMenuItem_item
          }
          ...SidebarSectionIcon_section
        }
      }
    }
  }
`);

interface AddToSectionMenuItemProps {
  item: DocumentType<typeof ITEM_FRAGMENT>;
}

const AddToSectionMenuItem = ({ item }: AddToSectionMenuItemProps) => {
  const dispatch = useAppDispatch();
  const { data } = useQuery(ADD_TO_SECTION_MENU_ITEM_QUERY);
  const sections = data?.currentSession.user.sidebarSections ?? [];
  const [updateSidebarSection] = useUpdateSidebarSection();

  const virtualSections = sections.filter(
    (section) =>
      !isPremadeSectionId(section.id) &&
      !section.items.map(({ id }) => id).includes(item.id),
  );

  const firstId = sections.at(0)?.id ?? PremadeSections.MY_TOPICS;

  return (
    <MenuItem
      icon={addIcon}
      subMenuItems={
        <>
          {virtualSections.map((section) => (
            <MenuItem
              key={section.id}
              icon={<SidebarSectionIcon section={section} />}
              onClick={async () => {
                await updateSidebarSection({
                  variables: {
                    id: section.id,
                    itemIds: [...section.items.map(({ id }) => id), item.id],
                  },
                });
              }}
            >
              {section.name}
            </MenuItem>
          ))}
          {virtualSections.length > 0 && <MenuSeparator />}
          <MenuItem
            onClick={() => {
              dispatch(UIActions.setSelectedSectionId(firstId));
            }}
            icon={editIcon}
          >
            Manage sections
          </MenuItem>
        </>
      }
    >
      Add to section
    </MenuItem>
  );
};

export default AddToSectionMenuItem;
